import { useDualMode } from "@mirage/service-experimentation/useDualMode";
import { tagged } from "@mirage/service-logging";
import { isWindowVisible } from "@mirage/service-platform-actions";
import { useEffect, useRef } from "react";

import type { TypeaheadSearch } from "@mirage/service-typeahead-search/hooks/useTypeaheadSearch";

const LAUNCHER_QUERY_TIMEOUT = 3 * 60 * 1000; // 3 minutes
const VISIBILITY_CHECK_INTERVAL = 1000;

const logger = tagged("useLauncherQueryManager");

export function useLauncherQueryManager({
  setTypeaheadQuery,
}: TypeaheadSearch) {
  const { isDualModeLauncher, getCurrentWindowName } = useDualMode();
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isVisibleRef = useRef<boolean>(true);

  useEffect(() => {
    if (!isDualModeLauncher) return;

    const checkWindowVisibility = async () => {
      try {
        const currentlyVisible = await isWindowVisible(getCurrentWindowName());

        // Detect visibility change
        if (isVisibleRef.current !== currentlyVisible) {
          if (!currentlyVisible) {
            // Window became hidden - start timeout
            timeoutRef.current = setTimeout(() => {
              setTypeaheadQuery("");
            }, LAUNCHER_QUERY_TIMEOUT);
          } else {
            // Window became visible - clear timeout
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
          }

          isVisibleRef.current = currentlyVisible;
        }
      } catch (error) {
        logger.error("Error checking window visibility:", error);
      }
    };

    // Start polling for visibility changes
    intervalRef.current = setInterval(
      checkWindowVisibility,
      VISIBILITY_CHECK_INTERVAL,
    );

    // Check initial state
    checkWindowVisibility();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isDualModeLauncher, setTypeaheadQuery, getCurrentWindowName]);
}
